import { motion } from "framer-motion";

const items = [
  {
    num: "01",
    title: "Research",
    body: "Frontier-aware experimentation across reasoning, retrieval and multi-modal systems — translated into reproducible engineering.",
    meta: "Reasoning · Retrieval · Multimodal",
  },
  {
    num: "02",
    title: "Open Source",
    body: "Tools, frameworks and primitives released to the community to compound trust, adoption and developer velocity.",
    meta: "Tools · Frameworks · Primitives",
  },
  {
    num: "03",
    title: "Products",
    body: "Practical AI products with immediate utility — built for real workflows, not demos. Designed to be usable, sovereign and deployable.",
    meta: "SaaS · On-prem · Sovereign",
  },
  {
    num: "04",
    title: "AI Systems",
    body: "Autonomous agents, orchestration layers and assistant-grade systems that generate, understand and act on knowledge in real environments.",
    meta: "Agents · Orchestration · Assistants",
  },
];

function Row({ item, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-15%" }}
      transition={{ duration: 1, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
      className="group relative grid grid-cols-12 gap-6 py-10 md:py-14 border-t border-white/10 cursor-pointer"
      data-cursor="hover"
      data-testid={`practice-row-${item.num}`}
    >
      <div className="col-span-2 md:col-span-1">
        <span className="font-mono text-xs md:text-sm tracking-[0.2em] text-[#8a8a8a] group-hover:text-[#d4ff00] transition-colors duration-500">
          /{item.num}
        </span>
      </div>
      <div className="col-span-10 md:col-span-5">
        <h3
          className="font-display tracking-tight text-[#8a8a8a] group-hover:text-white transition-colors duration-500"
          style={{ fontSize: "clamp(2rem, 5.4vw, 5rem)", lineHeight: 0.95 }}
        >
          {item.title}
        </h3>
      </div>
      <div className="col-span-12 md:col-span-5 md:col-start-8 flex flex-col justify-end">
        <p className="text-base md:text-lg text-[#8a8a8a] group-hover:text-white transition-colors duration-500 font-light leading-relaxed max-w-md">
          {item.body}
        </p>
        <span className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-[#4a4a4a] group-hover:text-[#d4ff00] transition-colors duration-500">
          {item.meta}
        </span>
      </div>

      {/* Hover line */}
      <span className="absolute left-0 right-0 bottom-0 h-px bg-[#d4ff00] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700 ease-out" />
    </motion.div>
  );
}

export default function PracticeSection() {
  return (
    <section
      id="practice"
      data-testid="practice-section"
      className="relative w-full py-32 md:py-48"
    >
      <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 xl:px-24">
        <div className="flex items-baseline justify-between mb-16 md:mb-24">
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#d4ff00]">
              /02
            </span>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#8a8a8a]">
              The Practice
            </span>
          </div>
          <span className="hidden md:inline font-mono text-xs uppercase tracking-[0.3em] text-[#8a8a8a]">
            Four surfaces / one thesis
          </span>
        </div>

        <h2
          className="font-display tracking-tight text-white max-w-5xl mb-20 md:mb-32"
          style={{ fontSize: "clamp(2.2rem, 6vw, 6rem)", lineHeight: 0.95 }}
          data-testid="practice-headline"
        >
          We work across <em className="not-italic text-[#4a4a4a]">research</em>,{" "}
          <em className="not-italic text-[#4a4a4a]">open source</em>,{" "}
          <em className="not-italic text-[#4a4a4a]">products</em> &amp;{" "}
          <em className="not-italic text-[#4a4a4a]">systems</em>.
        </h2>

        <div className="border-b border-white/10">
          {items.map((it, i) => (
            <Row key={it.num} item={it} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
